'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function RmdCalculator() {
  const t = useTranslations("tools.rmdCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    birthYear: 1950,
    rmdYear: 2025,
    accountBalance: 200000,
    spouseBeneficiary: 'yes',
    spouseBirthYear: 1952,
    returnRate: 5
  });

  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');
  const [showProjections, setShowProjections] = useState(false);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const uniformLifetimeTable: { [key: number]: number } = {
    72: 27.4, 73: 26.5, 74: 25.5, 75: 24.6, 76: 23.7, 77: 22.9, 78: 22.0, 79: 21.1,
    80: 20.2, 81: 19.4, 82: 18.5, 83: 17.7, 84: 16.8, 85: 16.0, 86: 15.2, 87: 14.4,
    88: 13.7, 89: 12.9, 90: 12.2, 91: 11.5, 92: 10.8, 93: 10.1, 94: 9.5, 95: 8.9,
    96: 8.4, 97: 7.8, 98: 7.3, 99: 6.8, 100: 6.4, 101: 6.0, 102: 5.6, 103: 5.2,
    104: 4.9, 105: 4.6, 106: 4.3, 107: 4.1, 108: 3.9, 109: 3.7, 110: 3.5, 111: 3.4,
    112: 3.3, 113: 3.1, 114: 3.0, 115: 2.9, 116: 2.8, 117: 2.7, 118: 2.5, 119: 2.3,
    120: 2.0
  };

  const getDistributionPeriod = (age: number, isSpouseBeneficiary: boolean, ownerAge: number, spouseAge: number) => {
    if (isSpouseBeneficiary && spouseAge < ownerAge - 10) {
      const ageDiff = ownerAge - spouseAge;
      const jointLifeTable: { [key: number]: number } = {
        11: 77.0, 12: 76.1, 13: 75.2, 14: 74.3, 15: 73.4, 16: 72.5, 17: 71.6,
        18: 70.7, 19: 69.8, 20: 68.9, 21: 68.0, 22: 67.1, 23: 66.2, 24: 65.3,
        25: 64.4, 26: 63.5, 27: 62.6, 28: 61.7, 29: 60.8, 30: 59.9, 31: 59.0,
        32: 58.1, 33: 57.2, 34: 56.3, 35: 55.4, 36: 54.5, 37: 53.6, 38: 52.7,
        39: 51.8, 40: 50.9
      };
      return jointLifeTable[Math.min(ageDiff, 40)] || 50.9;
    }

    if (age >= 120) return 2.0;
    if (age < 72) return uniformLifetimeTable[72];
    return uniformLifetimeTable[age] || 2.0;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    }).format(num);
  };

  const generateProjections = (startAge: number, startYear: number, startBalance: number, returnRate: number, isSpouseBeneficiary: boolean, startSpouseAge: number) => {
    const projections: any[] = [];
    let balance = startBalance;
    let currentAge = startAge;
    let currentYear = startYear;
    let spouseAge = startSpouseAge;

    for (let i = 0; i < 10 && currentAge <= 120; i++) {
      const distributionPeriod = getDistributionPeriod(currentAge, isSpouseBeneficiary, currentAge, spouseAge);
      const rmd = balance / distributionPeriod;

      projections.push({
        age: currentAge,
        year: currentYear,
        balance: balance,
        rmd: rmd
      });

      balance = (balance - rmd) * (1 + returnRate / 100);
      currentAge++;
      currentYear++;
      spouseAge++;

      if (balance <= 0) break;
    }

    return projections;
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const age = inputs.rmdYear - inputs.birthYear;
    const spouseAge = inputs.spouseBeneficiary === 'yes' ? inputs.rmdYear - inputs.spouseBirthYear : 0;

    if (age < 73) {
      setError(t('errorAgeTooYoung'));
      setResults(null);
      return;
    }

    if (inputs.accountBalance <= 0) {
      setError(t('errorInvalidBalance'));
      setResults(null);
      return;
    }

    const distributionPeriod = getDistributionPeriod(age, inputs.spouseBeneficiary === 'yes', age, spouseAge);
    const rmdAmount = inputs.accountBalance / distributionPeriod;

    let projections: any[] = [];
    if (inputs.returnRate > 0) {
      projections = generateProjections(age, inputs.rmdYear, inputs.accountBalance, inputs.returnRate, inputs.spouseBeneficiary === 'yes', spouseAge);
      setShowProjections(true);
    } else {
      setShowProjections(false);
    }

    setResults({
      rmdAmount,
      age,
      distributionPeriod,
      projections
    });
  };

  const handleClear = () => {
    setInputs({
      birthYear: 1950,
      rmdYear: 2025,
      accountBalance: 200000,
      spouseBeneficiary: 'yes',
      spouseBirthYear: 1952,
      returnRate: 5
    });
    setResults(null);
    setError('');
    setShowProjections(false);
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { margin: '0 auto', background: 'white', borderRadius: '1rem', boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)', overflow: 'hidden' as const },
    { maxWidth: '100%' },
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)', color: 'white', textAlign: 'center' as const },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const toolHeaderH2Style = getResponsiveStyle(
    { margin: 0, fontWeight: 700, color: 'white' },
    { fontSize: '1.5rem' },
    { fontSize: '1.625rem' },
    { fontSize: '1.75rem' }
  );

  const formStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem' },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const formRowStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const formGroupStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem'
  };

  const labelStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      background: 'white',
      width: '100%'
    },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.8rem 1rem', fontSize: '1rem' },
    { padding: '0.875rem 1rem', fontSize: '1rem' }
  );

  const selectStyle = { ...inputStyle };

  const radioGroupStyle: React.CSSProperties = {
    display: 'flex',
    gap: '1.5rem',
    marginTop: '0.5rem'
  };

  const radioOptionStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    cursor: 'pointer'
  };

  const buttonGroupStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', marginTop: '1rem' },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const calculateBtnStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white'
    },
    { padding: '1rem', fontSize: '1rem', minHeight: '44px' },
    { padding: '1rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1rem' }
  );

  const clearBtnStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: '#f1f5f9',
      color: '#475569'
    },
    { padding: '1rem', fontSize: '1rem', minHeight: '44px' },
    { padding: '1rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      borderTop: '3px solid #f59e0b'
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { margin: '0 0 1.5rem 0', color: '#92400e', display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const primaryResultStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '0.75rem',
      marginBottom: '1.5rem',
      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
    },
    { padding: '1.25rem' },
    { padding: '1.375rem' },
    { padding: '1.5rem' }
  );

  const primaryResultValueStyle = getResponsiveStyle(
    { fontWeight: 700, color: '#2563eb', margin: '0.5rem 0' },
    { fontSize: '2rem' },
    { fontSize: '2.25rem' },
    { fontSize: '2.5rem' }
  );

  const primaryResultLabelStyle = getResponsiveStyle(
    { color: '#64748b' },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' }
  );

  const resultDetailsStyle: React.CSSProperties = {
    display: 'grid',
    gap: '1rem',
    marginTop: '1.5rem'
  };

  const resultItemStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '0.5rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    { padding: '0.875rem' },
    { padding: '0.9375rem' },
    { padding: '1rem' }
  );

  const resultLabelStyle = getResponsiveStyle(
    { color: '#475569', fontWeight: 500 },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' }
  );

  const resultValueStyle = getResponsiveStyle(
    { color: colors.textPrimary, fontWeight: 700 },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const formulaDisplayStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '0.75rem',
      margin: '1.5rem 0',
      textAlign: 'center' as const
    },
    { padding: '1.25rem' },
    { padding: '1.375rem' },
    { padding: '1.5rem' }
  );

  const formulaStyle = getResponsiveStyle(
    { color: colors.textPrimary, fontFamily: "'Courier New', monospace" },
    { fontSize: '1rem' },
    { fontSize: '1.1rem' },
    { fontSize: '1.2rem' }
  );

  const projectionTableStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'white',
      borderRadius: '0.75rem',
      overflow: 'auto' as const,
      marginTop: '1rem'
    },
    {},
    {},
    {}
  );

  const tableStyle: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse'
  };

  const thStyle = getResponsiveStyle(
    {
      background: '#2563eb',
      color: 'white',
      textAlign: 'left' as const,
      fontWeight: 600
    },
    { padding: '0.75rem', fontSize: '0.875rem' },
    { padding: '0.875rem', fontSize: '0.9375rem' },
    { padding: '1rem', fontSize: '1rem' }
  );

  const tdStyle = getResponsiveStyle(
    {
      borderBottom: '1px solid #e2e8f0'
    },
    { padding: '0.625rem 0.75rem', fontSize: '0.875rem' },
    { padding: '0.6875rem 0.875rem', fontSize: '0.9375rem' },
    { padding: '0.75rem 1rem', fontSize: '1rem' }
  );

  const errorMessageStyle = getResponsiveStyle(
    {
      background: '#fee2e2',
      color: '#991b1b',
      borderRadius: '0.5rem',
      marginTop: '1rem'
    },
    { padding: '0.875rem', fontSize: '0.9rem' },
    { padding: '0.9375rem', fontSize: '0.95rem' },
    { padding: '1rem', fontSize: '1rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.75rem', marginTop: '1.5rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.7, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.0625rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = getResponsiveStyle(
    { lineHeight: 1.7, color: colors.textSecondary, marginLeft: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.0625rem' },
    { fontSize: '1.125rem' }
  );

  const tooltipIconStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '18px',
    height: '18px',
    background: '#e0e7ff',
    color: '#4f46e5',
    borderRadius: '50%',
    fontSize: '0.75rem',
    cursor: 'help',
    fontWeight: 700
  };

  const refLinkStyle: React.CSSProperties = {
    color: colors.primary,
    textDecoration: 'none',
    wordBreak: 'break-word'
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderH2Style}>🧮 {t('toolTitle')}</h2>
          </div>

          <form style={formStyle} onSubmit={handleCalculate}>
            <div style={formRowStyle}>
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="birthYear">{t('birthYearLabel')}</label>
                <input
                  style={inputStyle}
                  type="number"
                  id="birthYear"
                  min="1900"
                  max="2024"
                  value={inputs.birthYear}
                  onChange={(e) => setInputs({...inputs, birthYear: parseInt(e.target.value)})}
                  required
                />
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="rmdYear">
                  {t('rmdYearLabel')}
                  <span style={tooltipIconStyle} title={t('rmdYearTooltip')}>?</span>
                </label>
                <select
                  style={selectStyle}
                  id="rmdYear"
                  value={inputs.rmdYear}
                  onChange={(e) => setInputs({...inputs, rmdYear: parseInt(e.target.value)})}
                  required
                >
                  <option value="2025">2025</option>
                  <option value="2026">2026</option>
                  <option value="2027">2027</option>
                  <option value="2028">2028</option>
                  <option value="2029">2029</option>
                  <option value="2030">2030</option>
                </select>
              </div>
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle} htmlFor="accountBalance">{t('accountBalanceLabel')}</label>
              <input
                style={inputStyle}
                type="number"
                id="accountBalance"
                min="0"
                step="0.01"
                value={inputs.accountBalance}
                onChange={(e) => setInputs({...inputs, accountBalance: parseFloat(e.target.value)})}
                required
              />
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>{t('spouseBeneficiaryLabel')}</label>
              <div style={radioGroupStyle}>
                <label style={radioOptionStyle}>
                  <input
                    type="radio"
                    name="spouseBeneficiary"
                    value="yes"
                    checked={inputs.spouseBeneficiary === 'yes'}
                    onChange={(e) => setInputs({...inputs, spouseBeneficiary: e.target.value})}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span>{t('yes')}</span>
                </label>
                <label style={radioOptionStyle}>
                  <input
                    type="radio"
                    name="spouseBeneficiary"
                    value="no"
                    checked={inputs.spouseBeneficiary === 'no'}
                    onChange={(e) => setInputs({...inputs, spouseBeneficiary: e.target.value})}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span>{t('no')}</span>
                </label>
              </div>
            </div>

            {inputs.spouseBeneficiary === 'yes' && (
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="spouseBirthYear">{t('spouseBirthYearLabel')}</label>
                <input
                  style={inputStyle}
                  type="number"
                  id="spouseBirthYear"
                  min="1900"
                  max="2024"
                  value={inputs.spouseBirthYear}
                  onChange={(e) => setInputs({...inputs, spouseBirthYear: parseInt(e.target.value)})}
                />
              </div>
            )}

            <div style={formGroupStyle}>
              <label style={labelStyle} htmlFor="returnRate">
                {t('returnRateLabel')}
                <span style={tooltipIconStyle} title={t('returnRateTooltip')}>?</span>
              </label>
              <input
                style={inputStyle}
                type="number"
                id="returnRate"
                min="0"
                max="100"
                step="0.01"
                value={inputs.returnRate}
                onChange={(e) => setInputs({...inputs, returnRate: parseFloat(e.target.value)})}
                placeholder={t('returnRatePlaceholder')}
              />
            </div>

            {error && (
              <div style={errorMessageStyle}>
                {error}
              </div>
            )}

            <div style={buttonGroupStyle}>
              <button type="submit" style={calculateBtnStyle}>{t('calculateButton')}</button>
              <button type="button" style={clearBtnStyle} onClick={handleClear}>{t('clearButton')}</button>
            </div>
          </form>

          {results && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>📊 {t('resultsTitle')}</h3>

              <div style={primaryResultStyle}>
                <div style={primaryResultLabelStyle}>
                  {t('rmdForYear')} <span>{inputs.rmdYear}</span> {t('is')}
                </div>
                <div style={primaryResultValueStyle}>{formatCurrency(results.rmdAmount)}</div>
              </div>

              <div style={resultDetailsStyle}>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('yourAgeIn')} <span>{inputs.rmdYear}</span></span>
                  <span style={resultValueStyle}>{results.age}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('distributionPeriod')}</span>
                  <span style={resultValueStyle}>{formatNumber(results.distributionPeriod)}</span>
                </div>
              </div>

              <div style={formulaDisplayStyle}>
                <div style={{ color: '#64748b', marginBottom: '0.5rem', fontSize: screenSize === 'mobile' ? '0.875rem' : '1rem' }}>
                  {t('calculationFormula')}
                </div>
                <div style={formulaStyle}>
                  RMD = {formatCurrency(inputs.accountBalance)} ÷ {formatNumber(results.distributionPeriod)} = {formatCurrency(results.rmdAmount)}
                </div>
              </div>

              {showProjections && results.projections.length > 0 && (
                <div style={{ marginTop: '2rem' }}>
                  <h4 style={{ color: '#92400e', marginBottom: '1rem', fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem' }}>
                    {t('futureProjectionsTitle')}
                  </h4>
                  <p style={{ color: '#78350f', marginBottom: '1rem', fontSize: screenSize === 'mobile' ? '0.9rem' : '1rem' }}>
                    {t('futureProjectionsDescription')} <span>{inputs.returnRate}</span>% {t('annualReturn')}
                  </p>
                  <div style={projectionTableStyle}>
                    <table style={tableStyle}>
                      <thead>
                        <tr>
                          <th style={thStyle}>{t('age')}</th>
                          <th style={thStyle}>{t('year')}</th>
                          <th style={thStyle}>{t('accountBalance')}</th>
                          <th style={thStyle}>{t('rmdAmount')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.projections.map((proj: any, index: number) => (
                          <tr key={index} style={{ background: index % 2 === 0 ? 'white' : '#f8fafc' }}>
                            <td style={tdStyle}><strong>{proj.age}</strong></td>
                            <td style={tdStyle}>{proj.year}</td>
                            <td style={tdStyle}>{formatCurrency(proj.balance)}</td>
                            <td style={tdStyle}><strong>{formatCurrency(proj.rmd)}</strong></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>{t('step1')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('step2')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('step3')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('step4')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('step5')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('step6')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insight1')}</p>
        <p style={paragraphStyle}>{t('insight2')}</p>
        <p style={paragraphStyle}>{t('insight3')}</p>
        <p style={paragraphStyle}>{t('insight4')}</p>
        <p style={paragraphStyle}>{t('insight5')}</p>
        <p style={paragraphStyle}>{t('insight6')}</p>
        <p style={paragraphStyle}>{t('insight7')}</p>
        <p style={paragraphStyle}>{t('insight8')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>

        <h3 style={h3Style}>{t('whatIsRmdTitle')}</h3>
        <p style={paragraphStyle}>{t('whatIsRmdContent')}</p>

        <h3 style={h3Style}>{t('whenStartTitle')}</h3>
        <p style={paragraphStyle}>{t('whenStartContent')}</p>

        <h3 style={h3Style}>{t('howCalculatedTitle')}</h3>
        <p style={paragraphStyle}>{t('howCalculatedContent')}</p>

        <h3 style={h3Style}>{t('penaltiesTitle')}</h3>
        <p style={paragraphStyle}>{t('penaltiesContent')}</p>

        <h3 style={h3Style}>{t('specialCasesTitle')}</h3>
        <p style={paragraphStyle}>{t('specialCasesContent')}</p>

        <h3 style={h3Style}>{t('taxImplicationsTitle')}</h3>
        <p style={paragraphStyle}>{t('taxImplicationsContent')}</p>

        <h3 style={h3Style}>{t('bestPracticesTitle')}</h3>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.5rem' }}>{t('practice1')}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t('practice2')}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t('practice3')}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t('practice4')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <h3 style={h3Style}>{t('faq1Question')}</h3>
        <p style={paragraphStyle}>{t('faq1Answer')}</p>

        <h3 style={h3Style}>{t('faq2Question')}</h3>
        <p style={paragraphStyle}>{t('faq2Answer')}</p>

        <h3 style={h3Style}>{t('faq3Question')}</h3>
        <p style={paragraphStyle}>{t('faq3Answer')}</p>

        <h3 style={h3Style}>{t('faq4Question')}</h3>
        <p style={paragraphStyle}>{t('faq4Answer')}</p>

        <h3 style={h3Style}>{t('faq5Question')}</h3>
        <p style={paragraphStyle}>{t('faq5Answer')}</p>

        <h3 style={h3Style}>{t('faq6Question')}</h3>
        <p style={paragraphStyle}>{t('faq6Answer')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>
            <a href="https://www.bankrate.com/retirement/required-minimum-distribution-rmd/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref1')}
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a href="https://www.redriverbank.net/calculators/retiredistrib" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref2')}
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a href="https://www.irs.gov/retirement-plans/retirement-plan-and-ira-required-minimum-distributions-faqs" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref3')}
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a href="https://www.fidelity.com/learning-center/personal-finance/first-rmd-requirements" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref4')}
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a href="https://investor.vanguard.com/investor-resources-education/retirement/what-are-rmds" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref5')}
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a href="https://www.aarp.org/money/retirement/required-minimum-distribution-calculator/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref6')}
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a href="https://www.finra.org/investors/insights/required-minimum-distributions" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref7')}
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a href="https://www.securitybenefit.com/individuals/article/when-do-i-take-required-minimum-distribution" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref8')}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}